import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mails, PenLine } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Empty } from "@/components/kit";
import { LetterComposer } from "@/components/letters/LetterComposer";
import { SealedLetter, ArrivedLetter, LetterView } from "@/components/letters/LetterCards";
import { OpenWhenComposer, OpenWhenEnvelope, OpenWhenView } from "@/components/letters/OpenWhen";

export default function Letters() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [writing, setWriting] = useState(null);
  const [reading, setReading] = useState(null);
  const [ow, setOw] = useState(null);
  const deepLinked = useRef(false);
  const [owWriting, setOwWriting] = useState(false);
  const [owReading, setOwReading] = useState(null);

  const loadOw = useCallback(() => api.get("/open-when").then((r) => setOw(r.data)).catch((e) => toast.error(errMsg(e))), []);
  const openOw = useCallback(async (l) => {
    setWriting(null); setReading(null); setOwWriting(false);
    try { const { data: full } = await api.post(`/open-when/${l.id}/open`); setOwReading(full); window.scrollTo({ top: 0, behavior: "smooth" }); loadOw(); }
    catch (e) { toast.error(errMsg(e, "Couldn't open that letter")); }
  }, [loadOw]);
  const removeOw = async (id) => {
    try { await api.delete(`/open-when/${id}`); toast.success("Letter removed"); setOwReading(null); loadOw(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => api.get("/letters").then((r) => { setData(r.data); return r.data; }).catch((e) => toast.error(errMsg(e))), []);

  const open = useCallback(async (l) => {
    setWriting(null);
    try {
      const { data: full } = await api.get(`/letters/${l.id}`);
      setReading(full);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!full.opened_at) { await api.post(`/letters/${l.id}/open`); load(); }
    } catch (e) { toast.error(errMsg(e, "Couldn't open that letter")); }
  }, [load]);

  useEffect(() => {
    loadOw();
    if (deepLinked.current) return;
    deepLinked.current = true;
    load().then((d) => {
      const id = params.get("open"), owId = params.get("ow");
      if ((id || owId) && d) setParams({}, { replace: true });
      if (id && d) open({ id });
      else if (owId) openOw({ id: owId });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try { await api.delete(`/letters/${id}`); toast.success("Letter removed"); setReading(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saved = () => { setWriting(null); setReading(null); load(); };

  const busyView = writing || reading || owWriting || owReading;
  if (!data || !ow) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-mm-primary" /></div>;
  return (
    <div className="max-w-3xl" data-testid="letters-page">
      <PageHeader back title="Letters to Future Me" subtitle="Write yourself a kind letter. Seal it for a future day, or keep Open-When letters ready for hard moments." />
      {!busyView && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button className="mm-btn" onClick={() => setWriting({})} data-testid="letters-write-btn"><PenLine size={16} />Write a letter</button>
          <button className="mm-btn-ghost" onClick={() => setOwWriting(true)} data-testid="open-when-write-btn"><Mails size={16} />Write an Open-When letter</button>
        </div>
      )}
      {owWriting && <div className="mb-8"><OpenWhenComposer topics={ow.topics} onSaved={() => { setOwWriting(false); loadOw(); }} onCancel={() => setOwWriting(false)} /></div>}
      {owReading && <div className="mb-8"><OpenWhenView l={owReading} onClose={() => setOwReading(null)} onDelete={removeOw} /></div>}
      {writing && <div className="mb-8"><LetterComposer replyTo={writing.replyTo} onSaved={saved} onCancel={() => setWriting(null)} /></div>}
      {reading && !writing && <div className="mb-8"><LetterView l={reading} onReply={(l) => setWriting({ replyTo: l.id })} onDelete={remove} onClose={() => setReading(null)} /></div>}
      {!busyView && (
        <>
          <section className="mb-8" data-testid="open-when-section">
            <h2 className="font-semibold">Open when…</h2>
            <p className="text-sm text-mm-muted mb-3">Letters you can open any time you need them. They stay here to re-read.</p>
            {ow.letters.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mm-stagger" data-testid="open-when-list">{ow.letters.map((l) => <OpenWhenEnvelope key={l.id} l={l} onOpen={openOw} />)}</div>
            ) : <button onClick={() => setOwWriting(true)} className="mm-card p-4 w-full text-left text-sm text-mm-muted hover:-translate-y-0.5 transition-transform" data-testid="open-when-empty">💌 Write one for "I can't sleep" or "I feel alone". Future you will be glad.</button>}
          </section>
          {!data.sealed.length && !data.arrived.length && (
            <div data-testid="letters-empty"><Empty emoji="💌" title="No letters yet" text="Write to the you of next month, next year or your next hard day. It'll be waiting." /></div>
          )}
          {data.arrived.length > 0 && (
            <section className="mb-8" data-testid="letters-arrived">
              <h2 className="font-semibold mb-3">Arrived</h2>
              <div className="space-y-2">{data.arrived.map((l) => <ArrivedLetter key={l.id} l={l} onOpen={open} />)}</div>
            </section>
          )}
          {data.sealed.length > 0 && (
            <section data-testid="letters-sealed">
              <h2 className="font-semibold mb-3">Sealed</h2>
              <div className="space-y-2">{data.sealed.map((l) => <SealedLetter key={l.id} l={l} onCancel={remove} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
